import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const VarianceAlerts = ({ reconciliations, threshold, onRefresh }) => {
  if (!reconciliations || reconciliations?.length === 0) {
    return null;
  }

  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-foreground flex items-center gap-2">
          <Icon name="AlertTriangle" size={24} className="text-red-500" />
          Variance Alerts ({reconciliations?.length})
        </h2>
        <Button
          onClick={onRefresh}
          variant="outline"
          size="sm"
          className="flex items-center gap-2"
        >
          <Icon name="RefreshCw" size={16} />
          Refresh
        </Button>
      </div>

      <div className="space-y-3">
        {reconciliations?.map((reconciliation) => {
          const variance = parseFloat(reconciliation?.actual_balance || 0) - parseFloat(reconciliation?.expected_balance || 0);
          const isLargeVariance = Math.abs(variance) > (threshold || 5);
          const hasAiAnalysis = reconciliation?.ai_analysis;
          const alertSent = reconciliation?.alert_sent;

          return (
            <div
              key={reconciliation?.id}
              className={`p-4 rounded-lg border ${
                isLargeVariance 
                  ? 'bg-red-500/10 border-red-500/20' :'bg-orange-500/10 border-orange-500/20'
              }`}
            >
              <div className="flex items-start gap-3">
                <Icon 
                  name={isLargeVariance ? 'AlertTriangle' : 'AlertCircle'} 
                  size={20} 
                  className={isLargeVariance ? 'text-red-500' : 'text-orange-500'} 
                />
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-bold text-foreground">
                      {reconciliation?.cash_drawers?.name || 'Drawer'}
                    </h3>
                    <span className={`text-sm font-bold ${
                      isLargeVariance ? 'text-red-500' : 'text-orange-500'
                    }`}>
                      {variance >= 0 ? '+' : ''}${variance?.toFixed(2)} variance
                    </span>
                  </div>

                  {/* Alert Status */}
                  <div className="flex items-center gap-2 mb-2">
                    {alertSent ? (
                      <span className="flex items-center gap-1 text-xs px-2 py-1 bg-green-500/10 text-green-500 rounded-full border border-green-500/20">
                        <Icon name="CheckCircle" size={14} />
                        Alert Sent
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-xs px-2 py-1 bg-yellow-500/10 text-yellow-500 rounded-full border border-yellow-500/20">
                        <Icon name="Clock" size={14} />
                        Pending Alert
                      </span>
                    )}
                    {hasAiAnalysis && (
                      <span className="flex items-center gap-1 text-xs px-2 py-1 bg-blue-500/10 text-blue-500 rounded-full border border-blue-500/20">
                        <Icon name="Brain" size={14} />
                        AI Analyzed
                      </span>
                    )}
                  </div>

                  <p className="text-sm text-muted-foreground mb-2">
                    {isLargeVariance 
                      ? 'Large variance detected - requires immediate manager attention' 
                      : 'Variance exceeds threshold - review required'}
                  </p>

                  <div className="flex items-center gap-4 text-xs text-muted-foreground mb-2">
                    <span>Expected: ${parseFloat(reconciliation?.expected_balance || 0)?.toFixed(2)}</span>
                    <span>Actual: ${parseFloat(reconciliation?.actual_balance || 0)?.toFixed(2)}</span>
                    <span>By: {reconciliation?.user_profiles?.full_name || 'Unknown'}</span>
                  </div>

                  {/* Variance Reason */}
                  {reconciliation?.variance_reason && (
                    <div className="mt-2 p-2 bg-background rounded border border-border">
                      <p className="text-xs text-muted-foreground mb-1">Reason:</p>
                      <p className="text-sm text-foreground">{reconciliation?.variance_reason}</p>
                    </div>
                  )}

                  {/* AI Analysis */}
                  {hasAiAnalysis && reconciliation?.ai_analysis && (
                    <div className="mt-3 p-3 bg-blue-500/5 border border-blue-500/10 rounded-lg">
                      <div className="flex items-start gap-2">
                        <Icon name="Sparkles" size={16} className="text-blue-500 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-xs font-semibold text-blue-500 mb-1">AI Analysis</p>
                          
                          {reconciliation?.ai_analysis?.riskLevel && (
                            <span className={`inline-block text-xs px-2 py-1 rounded-full font-medium mb-2 ${
                              reconciliation?.ai_analysis?.riskLevel === 'critical' ? 'bg-red-500/10 text-red-500' :
                              reconciliation?.ai_analysis?.riskLevel === 'high' ? 'bg-orange-500/10 text-orange-500' :
                              reconciliation?.ai_analysis?.riskLevel === 'medium'? 'bg-yellow-500/10 text-yellow-500' : 'bg-green-500/10 text-green-500'
                            }`}>
                              {reconciliation?.ai_analysis?.riskLevel?.toUpperCase()} RISK
                            </span>
                          )}

                          {reconciliation?.ai_analysis?.causes?.length > 0 && (
                            <div className="mb-2">
                              <p className="text-xs font-medium text-foreground mb-1">Possible Causes:</p>
                              <ul className="text-xs text-muted-foreground space-y-1">
                                {reconciliation?.ai_analysis?.causes?.slice(0, 3)?.map((cause, idx) => (
                                  <li key={idx} className="flex items-start gap-1">
                                    <span className="text-blue-500 mt-0.5">•</span>
                                    <span>{cause}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {reconciliation?.ai_analysis?.recommendations?.length > 0 && (
                            <div>
                              <p className="text-xs font-medium text-foreground mb-1">Recommendations:</p>
                              <ul className="text-xs text-muted-foreground space-y-1">
                                {reconciliation?.ai_analysis?.recommendations?.slice(0, 2)?.map((rec, idx) => (
                                  <li key={idx} className="flex items-start gap-1">
                                    <span className="text-blue-500 mt-0.5">•</span>
                                    <span>{rec}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {reconciliation?.ai_analysis?.suspiciousPatterns?.length > 0 && (
                            <div className="mt-2 p-2 bg-red-500/10 border border-red-500/20 rounded">
                              <p className="text-xs font-semibold text-red-500 mb-1">⚠️ Suspicious Patterns:</p>
                              <ul className="text-xs text-foreground space-y-1">
                                {reconciliation?.ai_analysis?.suspiciousPatterns?.map((pattern, idx) => (
                                  <li key={idx}>• {pattern}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default VarianceAlerts;